import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, MinusIcon, XIcon } from 'lucide-react';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useUser } from '@/hooks/auth/useUser';
import type { TripApprovalStep } from '@/hooks/trips/types';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n';

type StepStatus = 'completed' | 'current' | 'upcoming' | 'rejected';

interface TripApprovalStepperProps {
  steps?: TripApprovalStep[];
  pendingApproverId?: number | null;
  className?: string;
  fullWidth?: boolean;
}

interface NormalizedStep {
  id: string;
  approverName: string;
  position: string;
  status: StepStatus;
  isCurrentUser: boolean;
  stepIndex: number;
}

function getStepStatus(
  rawState: string | null | undefined,
  pendingApproverId: number | null | undefined,
  approverId: number
): StepStatus {
  const normalized = (rawState ?? '').toLowerCase().trim();

  if (normalized === 'rejected' || normalized === 'declined') {
    return 'rejected';
  }

  if (normalized === 'approved' || normalized === 'done' || normalized === 'completed') {
    return 'completed';
  }

  if (
    normalized === 'pending' ||
    normalized === 'in_progress' ||
    normalized === 'processing' ||
    (pendingApproverId != null && approverId === pendingApproverId)
  ) {
    return 'current';
  }

  return 'upcoming';
}

function StepIcon({ status }: { status: StepStatus }) {
  if (status === 'completed') {
    return <img src="/assets/icons/check.svg" alt="" className="h-4 w-4 text-white" />;
  }

  if (status === 'current') {
    return <Spinner variant="circle" className="text-white" size={14} strokeWidth={2.5} />;
  }

  if (status === 'rejected') {
    return <XIcon className="h-4 w-4 text-white" strokeWidth={2.5} />;
  }

  return <MinusIcon className="h-4 w-4 text-[#292929]" strokeWidth={2.5} />;
}

export default function TripApprovalStepper({
  steps = [],
  pendingApproverId,
  className,
  fullWidth = false,
}: TripApprovalStepperProps) {
  const { data: user } = useUser();
  const t = useTranslations('trips.approvalStepper');
  const tActivity = useTranslations('trips.activity');
  const currentUserId = user?.id ?? null;

  const normalizedSteps = useMemo<NormalizedStep[]>(() => {
    return steps
      .filter((step) => step != null)
      .map((step, index) => {
        const identifier =
          step.id != null && step.id !== 0 ? String(step.id) : `approval-step-${index}`;
        const approverName =
          typeof step.approverName === 'string' && step.approverName.trim().length > 0
            ? step.approverName
            : '—';
        const position =
          typeof step.approverPosition === 'string' && step.approverPosition.trim().length > 0
            ? step.approverPosition
            : '—';

        const status = getStepStatus(step.state, pendingApproverId, Number(step.approverId));
        const isCurrentUser = currentUserId != null && String(step.approverId) === currentUserId;

        return {
          id: identifier,
          approverName,
          position,
          status,
          isCurrentUser,
          stepIndex: index,
        };
      });
  }, [currentUserId, pendingApproverId, steps]);

  const [showAll, setShowAll] = useState(false);
  const desktopScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isDesktopScrollable, setIsDesktopScrollable] = useState(false);

  useEffect(() => {
    const updateScrollableState = () => {
      const container = desktopScrollContainerRef.current;

      if (!container) {
        setIsDesktopScrollable(false);
        return;
      }

      const { scrollWidth, clientWidth } = container;
      setIsDesktopScrollable(scrollWidth - clientWidth > 2);
    };

    updateScrollableState();

    const container = desktopScrollContainerRef.current;
    let resizeObserver: ResizeObserver | null = null;

    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateScrollableState();
      });
      resizeObserver.observe(container);
    } else {
      window.addEventListener('resize', updateScrollableState);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateScrollableState);
      }
    };
  }, [normalizedSteps, fullWidth]);

  const currentIndex = normalizedSteps.findIndex((step) => step.status === 'current');

  const visibleSteps = useMemo(() => {
    if (!normalizedSteps.length) {
      return normalizedSteps;
    }

    if (showAll || normalizedSteps.length <= 1) {
      return normalizedSteps;
    }

    if (currentIndex !== -1) {
      return [normalizedSteps[currentIndex]];
    }

    return [normalizedSteps[0]];
  }, [currentIndex, normalizedSteps, showAll]);

  const canToggle = normalizedSteps.length > 1;
  const toggleLabel = showAll ? tActivity('collapse') : tActivity('expand');
  const desktopListClasses = cn(
    'flex h-full w-full flex-nowrap items-start gap-4 sm:gap-6',
    isDesktopScrollable ? 'justify-start' : 'justify-center'
  );

  if (!normalizedSteps.length) {
    return null;
  }

  const containerClasses = cn(
    'flex min-h-[9.8125rem] items-center overflow-x-auto border border-[#f1f1f1] bg-[#f9f9f9] px-4 py-5 sm:px-8',
    fullWidth ? 'relative left-1/2 w-screen -translate-x-1/2 rounded-none' : 'rounded-3xl',
    className
  );

  return (
    <>
      <div ref={desktopScrollContainerRef} className={cn(containerClasses, 'hidden lg:flex')}>
        <div className="mx-auto h-[9.8125rem] w-full max-w-[76.9375rem]">
          <ol className={desktopListClasses}>
            {normalizedSteps.map((step, index) => {
              const circleClasses = cn(
                'relative flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-white shadow-[0px_8px_24px_-12px_rgba(31,31,31,0.36)] transition-colors',
                step.status === 'completed' && 'bg-[#30a46c] text-white',
                step.status === 'current' && 'bg-[#ed8a5c] text-white',
                step.status === 'upcoming' && 'bg-[#f1f1f1]',
                step.status === 'rejected' && 'bg-[#e5484d] text-white'
              );

              const isLast = index === normalizedSteps.length - 1;

              return (
                <Fragment key={step.id}>
                  <li className="flex min-w-[8.5rem] flex-none items-start gap-4 pt-5 text-center sm:gap-6">
                    <div className="flex flex-col items-center gap-3">
                      <div className="relative flex flex-col items-center">
                        <div className={circleClasses}>
                          <StepIcon status={step.status} />
                        </div>
                        {step.isCurrentUser && (
                          <div className="absolute -top-2 left-8">
                            <div className="rounded-[24px] border border-[#f1f1f1] bg-[rgba(0,0,0,0.04)] px-2 py-1 text-[13px] leading-5 font-medium text-[#292929]">
                              {t('you')}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex max-w-[10rem] flex-col items-center gap-1">
                        <span className="text-[15px] leading-6 font-medium text-[#292929]">
                          {step.approverName}
                        </span>
                        <span className="line-clamp-3 text-center text-sm leading-5 text-[#666666]">
                          {step.position}
                        </span>
                      </div>
                    </div>
                    {!isLast && (
                      <div className="mt-14 flex items-center">
                        <div className="w-4 border-t border-[#bbbbbb] sm:w-6" />
                      </div>
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ol>
        </div>
      </div>

      <div
        className={cn(
          'relative w-full border-y border-[#f1f1f1] bg-[#f9f9f9] px-4 pt-3 pb-3 lg:hidden',
          fullWidth ? 'left-1/2 w-screen -translate-x-1/2 rounded-none' : '',
          className
        )}
      >
        <div
          className={cn(
            'relative pb-1 transition-all duration-300 ease-in-out',
            canToggle
              ? showAll
                ? 'max-h-[999px] opacity-100'
                : 'max-h-[13rem] overflow-hidden opacity-100'
              : 'opacity-100'
          )}
        >
          <ol className="flex flex-col gap-3">
            {visibleSteps.map((step, index) => {
              const circleClasses = cn(
                'relative flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-white shadow-[0px_4px_16px_-8px_rgba(31,31,31,0.28)] transition-colors',
                step.status === 'completed' && 'bg-[#30a46c] text-white',
                step.status === 'current' && 'bg-[#ed8a5c] text-white',
                step.status === 'upcoming' && 'bg-[#f1f1f1]',
                step.status === 'rejected' && 'bg-[#e5484d] text-white'
              );

              const isFirst = index === 0;
              const isLast = index === visibleSteps.length - 1;

              return (
                <li key={step.id} className="relative flex min-w-0 items-start gap-5 pl-14">
                  <div className="absolute top-0 left-3 flex min-h-full flex-col items-center">
                    {!isFirst && (
                      <span className="pointer-events-none mb-1 w-[2px] flex-1 bg-[repeating-linear-gradient(to_bottom,_rgba(217,217,217,0.6)_0,_rgba(217,217,217,0.6)_6px,_rgba(217,217,217,0.15)_6px,_rgba(217,217,217,0.15)_12px)]" />
                    )}
                    <span className={circleClasses}>
                      <StepIcon status={step.status} />
                    </span>
                    {!isLast && (
                      <span className="pointer-events-none mt-1 w-[2px] flex-1 bg-[repeating-linear-gradient(to_bottom,_rgba(217,217,217,0.6)_0,_rgba(217,217,217,0.6)_6px,_rgba(217,217,217,0.15)_6px,_rgba(217,217,217,0.15)_12px)]" />
                    )}
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[15px] leading-6 font-medium text-[#292929]">
                        {step.approverName}
                      </span>
                      {step.isCurrentUser && (
                        <span className="rounded-[18px] border border-[#f1f1f1] bg-[rgba(0,0,0,0.04)] px-2 py-0.5 text-[12px] leading-4 font-medium text-[#292929]">
                          {t('you')}
                        </span>
                      )}
                    </div>
                    <span className="text-sm leading-5 break-words text-[#666666]">
                      {step.position}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div className={cn('mt-2 flex justify-end', !canToggle && 'hidden')}>
          <button
            type="button"
            className="flex items-center gap-2 rounded-full border border-[#e4e4e4] bg-white px-4 py-2 text-sm font-medium text-[#292929] transition-transform hover:scale-[0.99]"
            onClick={() => setShowAll((prev) => !prev)}
            aria-label={toggleLabel}
            aria-expanded={showAll}
          >
            <span>{showAll ? t('hide') : t('showAll')}</span>
            <ChevronDownIcon
              className={cn('h-5 w-5 transition-transform duration-200', showAll && 'rotate-180')}
            />
          </button>
        </div>
      </div>
    </>
  );
}
